<template>
  <div class="container-fluid py-4">
    <!-- Header + Actions -->
    <div class="row mb-4">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <h5 class="text-dark mb-0 fw-bold">Salaries</h5>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" @click="exportToExcel">
            <i class="fas fa-file-export me-1"></i> Export
          </button>
          <router-link to="/add-salary">
            <button class="btn text-white btn-cap">
              <i class="fas fa-plus-circle me-1"></i> Add Salary
            </button>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Card -->
    <div class="card shadow-sm border-0 mb-4">
      <!-- CAP header -->
      <div class="card-header cap-header mx-3 mt-3" >
      
        <h6 class="mb-0 text-white fw-bold">Salaries Table</h6>
      </div>

      <!-- Search + Filter -->
      <div class="card-body border-bottom">
        <div class="row g-2 align-items-center">
          <div class="col-md-6">
            <div class="input-group search-wrap">
              <span class="input-group-text bg-white border-end-0">
                <i class="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                v-model="searchQuery"
                class="form-control border-start-0"
                :placeholder="$t('common.placeholders.searchByTitle')"
              />
            </div>
          </div>
          <div class="col-md-6 d-flex justify-content-md-end">
            <select v-model="statusFilter" class="form-select" style="width:auto;">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover m-0">
            <thead class="bg-light">
              <tr>
                <th class="ps-4 th-label">Title</th>
                <th class="th-label">Date</th>
                <th class="th-label">Status</th>
                <th class="th-label">Document</th>
                <th class="th-label text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="5" class="text-center py-4">
                  <i class="fas fa-spinner fa-spin me-2"></i> Loading...
                </td>
              </tr>

              <tr v-else-if="filteredSalaries.length === 0">
                <td colspan="5" class="text-center py-4 text-muted">
                  <i class="fas fa-info-circle me-2"></i> No salaries found.
                </td>
              </tr>

              <template v-else>
                <tr v-for="salary in filteredSalaries" :key="salary.id">
                  <td class="ps-4"><div class="title-cell">{{ salary.title }}</div></td>
                  <td><span class="text-muted">{{ salary.date }}</span></td>
                  <td>
                    <span class="badge rounded-pill" :class="salary.active ? 'bg-success' : 'bg-secondary'">
                      {{ salary.active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>
                    <a v-if="salary.document_url" :href="salary.document_url" target="_blank" class="btn btn-sm btn-outline-dark">
                      <i class="fas fa-download me-1"></i> Download
                    </a>
                    <span v-else class="text-muted small">—</span>
                  </td>
                  <td>
                    <div class="d-flex justify-content-center gap-2">
                      <!-- Toggle (même icône que BonusList) -->
                      <button
                        class="btn btn-ico btn-outline-secondary"
                        :title="salary.active ? 'Deactivate' : 'Activate'"
                        @click="toggleStatus(salary)"
                      >
                        <i class="fas" :class="salary.active ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                      </button>

                      <!-- Edit -->
                      <router-link
                        :to="`/edit-salary/${salary.id}`"
                        class="btn btn-ico btn-outline-primary"
                        :title="$t('common.tooltips.editSalary')"
                      >
                        <i class="fas fa-pen"></i>
                      </router-link>

                      <!-- Delete -->
                      <button
                        class="btn btn-ico btn-outline-danger"
                        :title="$t('common.tooltips.deleteSalary')"
                        @click="deleteSalary(salary.id)"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="card-body border-top d-flex justify-content-between align-items-center">
        <div class="small text-muted">
          Page {{ pagination.current_page }} / {{ pagination.last_page }}
          • Items: {{ pagination.total }}
        </div>
        <div class="btn-group">
          <button class="btn btn-outline-secondary" :disabled="pagination.current_page <= 1 || loading" @click="goToPage(pagination.current_page - 1)">Prev</button>
          <button class="btn btn-outline-secondary" :disabled="pagination.current_page >= pagination.last_page || loading" @click="goToPage(pagination.current_page + 1)">Next</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import axios from '@/api'
import * as XLSX from 'xlsx'

const toast = useToast()
const salaries = ref([])
const loading = ref(true)
const searchQuery = ref('')
const statusFilter = ref('all')

// Pagination (Laravel paginator)
const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 20,
  total: 0,
})

onMounted(async () => {
  await fetchSalaries(1)
})

function normalizeSalary(s) {
  const asBool = (v) => v === true || v === 1 || v === '1'
  const buildStorageUrl = (p) => {
    if (!p) return null
    try {
      const base = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '') || 'http://127.0.0.1:8000'
      return `${base}/storage/${p}`
    } catch {
      return null
    }
  }
  return {
    id: s.id,
    title: s.title,
    date: s.date,
    active: asBool(s.active),
    document_url: s.document_url ?? buildStorageUrl(s.document_path),
  }
}

const fetchSalaries = async (page = 1) => {
  try {
    loading.value = true
    const { data } = await axios.get('/financial-requests/salaries', { params: { page } })
    const p = data?.salaries || {}
    const rows = Array.isArray(p.data) ? p.data : []
    salaries.value = rows.map(normalizeSalary)
    pagination.value = {
      current_page: p.current_page ?? page,
      last_page: p.last_page ?? 1,
      per_page: p.per_page ?? 20,
      total: p.total ?? data?.total ?? rows.length,
    }
  } catch (e) {
    console.error('Error fetching salaries:', e)
    toast.error(e?.response?.data?.message || 'Failed to load salaries')
  } finally {
    loading.value = false
  }
}

const goToPage = (page) => {
  if (page < 1 || page > pagination.value.last_page) return
  fetchSalaries(page)
}

const filteredSalaries = computed(() =>
  salaries.value.filter(s => {
    const titleMatch = (s.title || '').toLowerCase().includes(searchQuery.value.toLowerCase())
    const statusMatch =
      statusFilter.value === 'all' ||
      (statusFilter.value === 'active' && s.active) ||
      (statusFilter.value === 'inactive' && !s.active)
    return titleMatch && statusMatch
  })
)

/* ✅ URL toggle alignée avec le pattern bonus:
   PATCH /financial-requests/salaries/{id}/toggle-status */
const toggleStatus = async (row) => {
  const prev = row.active
  row.active = !row.active
  try {
    const res = await axios.patch(`/financial-requests/salaries/${row.id}/toggle-status`)
    const apiActive = res?.data?.data?.active
    if (apiActive !== undefined) row.active = apiActive === true || apiActive === 1 || apiActive === '1'
    toast.success(`Salary ${row.active ? 'activated' : 'deactivated'} successfully`)
  } catch (err) {
    row.active = prev
    console.error('Error toggling status:', err)
    toast.error(err?.response?.data?.message || 'Failed to update status')
  }
}

const deleteSalary = async (id) => {
  if (!confirm('Are you sure you want to delete this salary?')) return
  try {
    await axios.delete(`/financial-requests/salaries/${id}`)
    salaries.value = salaries.value.filter(s => s.id !== id)
    if (salaries.value.length === 0 && pagination.value.current_page > 1) {
      await fetchSalaries(pagination.value.current_page - 1)
    }
    toast.success('Salary deleted successfully')
  } catch (e) {
    console.error('Error deleting salary:', e)
    toast.error(e?.response?.data?.message || 'Failed to delete salary')
  }
}

const exportToExcel = () => {
  const worksheet = XLSX.utils.json_to_sheet(
    filteredSalaries.value.map(s => ({
      Title: s.title,
      Date: s.date,
      Status: s.active ? 'Active' : 'Inactive',
      Document: s.document_url || '',
    }))
  )
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Salaries')
  XLSX.writeFile(workbook, 'Salaries_Export.xlsx')
}
</script>

<style scoped>
.cap-header {
  background: linear-gradient(195deg, #4b5563, #8d2e38);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 0.75rem 1rem;
}
.btn-cap { background-color: #4b5563; border-color: #b03a45; }
.btn-cap:hover { background-color: #4b5563; border-color: #8d2e38; color: #fff; }

.search-wrap .input-group-text,
.search-wrap .form-control,
.form-select { padding: 0.6rem 1rem; border-color: #e9ecef; }

.th-label { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.03em; font-weight: 600; }
.title-cell { font-weight: 600; color: #2b2b2b; }

.btn-ico{
  width:40px;height:40px;padding:0;
  display:inline-flex;align-items:center;justify-content:center;
  border-radius:.5rem;font-size:1.05rem;
}

.card { border-radius: 12px; overflow: hidden; }
.table td { vertical-align: middle; padding-top: 0.75rem; padding-bottom: 0.75rem; }
.badge { font-weight: 600; padding: 0.35em 0.65em; }
.container-fluid { max-width: 1320px; }
</style>
